import { toast, ToastContainer } from "react-toastify"
import { Lead } from "../../../models/Lead"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../../state/store"
import { LoadingMask } from "../../LoadingMask"
import { useEffect, useMemo, useState } from "react"
import { DocumentEnvelope } from "../../../models/ConsentDocument"
import FilteredTable from "../../FilteredTable"
import { longLocalDateFormat } from "../../../utils/functions"
import Button from "../../Button"
import { ArrowPathIcon, DocumentArrowDownIcon, NoSymbolIcon, RectangleStackIcon } from "@heroicons/react/24/solid"
import { LeadConsentDocumentModal } from "./LeadConsentDocumentModal"
import { VoidLeadConsentDocumentModal } from "./VoidLeadConsentDocumentModal"
import { fetchLeadDocumentEnvelopes, getEnvelopeDownloadDocument } from "../../../state/consentDocuments/actions"

interface Props {
    lead: Lead
    canEditLead: boolean
}

export const LeadConsentDocumentsModule = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [consentDocuments, setConsentDocuments] = useState<DocumentEnvelope[]>([]);
    const leadConsentDocuments = useSelector((state: RootState) => state.consentDocuments.leadEnvelopes);
    const [isLoadingConsentDocuments, setIsLoadingConsentDocuments] = useState(false);
    const isLoadingDownload = useSelector((state: RootState) => state.consentDocuments.isLoadingDownload);

    const [leadConsentDocumentModal, setLeadConsentDocumentModal] = useState<{
        show: boolean;
        lead?: Lead;
    }>({ show: false });
      
    const [ showVoidLeadConsentDocumentModal, setShowVoidLeadConsentDocumentModal] = useState<{
        consentDocument?: DocumentEnvelope;
        lead?: Lead;
        show: boolean;
    }>({ show: false, lead: undefined, consentDocument: undefined });

    useEffect(() => {
        //TODO: Review state and get data
        if(props.lead) {
            fetchConsentDocuments()
        }
    }, [props.lead]);

    const fetchConsentDocuments = () => {
        setIsLoadingConsentDocuments(true);
        dispatch(fetchLeadDocumentEnvelopes(props.lead.id!)).then((e) => {
          let consentDocuments = (e.payload || []) as DocumentEnvelope[];
          setConsentDocuments(consentDocuments);
          setIsLoadingConsentDocuments(false);
        });
    }

    const addConsentDocument = (consentDocument: DocumentEnvelope) => {
        setConsentDocuments(documents => [...documents, consentDocument])
    }
      
    const voidConsentDocument = (consentDocuments: DocumentEnvelope[]) => {
        //TODO: remove address from database
        setConsentDocuments(consentDocuments);
    }
      
    const downloadEnvelopeDocument = (envelope: DocumentEnvelope) => {
        dispatch(getEnvelopeDownloadDocument(envelope.docusign_envelope_id)).then(
          async (e: any) => {
            if (e.type === "getDownloadDocument/rejected") {
              toast(t("COULDNT_DOWNLOAD_DOCUMENT"));
            } else {
              const data = e.payload.download_documents;
              const nameSplit = envelope.template_document_name!.split(".");
              const filename = `${nameSplit[0]} - ${props.lead.first_name} ${props.lead.last_name}.pdf`;
    
              var binaryLen = data.length;
              var bytes = new Uint8Array(binaryLen);
              for (var i = 0; i < binaryLen; i++) {
                var ascii = data.charCodeAt(i);
                bytes[i] = ascii;
              }
    
              var blob = new Blob([bytes], { type: "application/pdf" });
              var link = window.URL.createObjectURL(blob);
    
              const aElement = document.createElement("a");
              document.body.appendChild(aElement);
              aElement.style.display = "none";
              aElement.href = link;
              aElement.download = filename;
              aElement.click();
              document.body.removeChild(aElement);
            }
          }
        );
    };

    const capitalize = (str: string) => {
        if (!str) return ''; // Si la cadena está vacía o es null, retorna una cadena vacía
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const columns = useMemo(() => {
        const showColumns = [
          {
              Header: t("NAME"),
              accessor: "template_name",
          },
          {
              Header: t("SENT_BY"),
              accessor: "agent_name",
          },
          {
              Header: t("SENT_DOCUMENT_DATE"),
              Cell: (row: any) => row.row.original.sent_date_time ? `${longLocalDateFormat(row.row.original.sent_date_time)}` : "-",
          },
          {
              Header: t("COMPLETED_DOCUMENT_DATE"),
              Cell: (row: any) => row.row.original.completed_date_time ? `${longLocalDateFormat(row.row.original.completed_date_time)}` : "-",
          },
          {
              Header: t("STATUS"),
              Cell: (row: any) => row.row.original.status === "completed"
                  ? `${capitalize(row.row.original.status)} (${t("SIGNED")})`
                  : `${capitalize(row.row.original.status)} (${t("NO_SIGNED")})`,
          }
        ]
    
        const actions = {
          id: "actions",
          Header: "",
          Cell: (row: any) => {
              return (
                  <div className="flex flex-row justify-center divide-x">
                      <Button
                          isTerciary
                          iconOnly
                          tooltip={t("DOWNLOAD_DOCUMENT")}
                          onClick={() => downloadEnvelopeDocument(row.row.original)}
                          classNames="mr-2"
                      >
                          <DocumentArrowDownIcon className="h-5 aspect-square text-blue" />
                      </Button>
                      <Button
                          isTerciary
                          iconOnly
                          isDisabled={
                              row.row.original.status === "completed" ||
                              row.row.original.status === "voided"
                          }
                          tooltip={t("VOID_DOCUMENT")}
                          onClick={() => setShowVoidLeadConsentDocumentModal({ show: true, lead: props.lead, consentDocument: row.row.original })}
                          classNames="mr-2"
                      >
                          <NoSymbolIcon className="h-5 aspect-square text-blue" />
                      </Button>
                  </div>
              );
          },
        }
    
        const backupActions = {
          id: "actions",
          Header: "",
          Cell: (row: any) => {
              return (
                  <div className="flex flex-row justify-center divide-x">
                      <Button
                          isTerciary
                          iconOnly
                          tooltip={t("DOWNLOAD_DOCUMENT")}
                          onClick={() => downloadEnvelopeDocument(row.row.original)}
                          classNames="mr-2"
                      >
                          <DocumentArrowDownIcon className="h-5 aspect-square text-blue" />
                      </Button>
                  </div>
              );
          },
        }
    
        if(!props.canEditLead) {
          return [...showColumns, backupActions]
        }
        
        return [...showColumns, actions]
    }, [voidConsentDocument, t, downloadEnvelopeDocument]);

    return (
        <div className="text-black max-h-full p-5 w-screen md:w-full relative overflow-x-auto bg-white rounded-lg shadow transition-all duration-500 ease-in-out overflow-hidden">
            <div className="text-xl font-bold text-black pl-3">{t("CONSENT_DOCUMENTS")}</div>
            {
                isLoadingConsentDocuments &&
                <div className="flex items-center justify-center pt-6 pb-5">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-gold"></div>
                </div>
            }
            {
                !isLoadingConsentDocuments && 
                <div className="pb-5">
                    <FilteredTable
                        columns={columns}
                        data={consentDocuments}
                        hasSearchBar={false}
                        buttonAction={
                            <Button
                                onClick={() => fetchConsentDocuments()}
                                tooltip={t('REFRESH')}
                                iconOnly
                                classNames="w-10 p-1 aspect-square mr-4 "
                                isTerciary={true}
                            >
                                <ArrowPathIcon className=""></ArrowPathIcon>
                            </Button>
                        }
                        secondaryActionButton={
                            props.canEditLead
                            ? <Button onClick={() => setLeadConsentDocumentModal({ show: true, lead: props.lead })}>
                                <span className="flex items-center font-semibold pr-3">
                                    <div className="w-8 p-1 aspect-square mr-2">
                                        <RectangleStackIcon />
                                    </div>
                                    {t("SEND_DOCUMENT")}
                                </span>
                            </Button>
                            : <div></div>
                        }
                    />
                </div>
            }
            {isLoadingDownload && <LoadingMask />}
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            <LeadConsentDocumentModal
                info={leadConsentDocumentModal}
                onSubmited={(consentDocument) => {
                    addConsentDocument(consentDocument)
                }}
                onClose={() => setLeadConsentDocumentModal({ show: false })}
            />
            <VoidLeadConsentDocumentModal
                info={showVoidLeadConsentDocumentModal}
                onClose={() => setShowVoidLeadConsentDocumentModal({show : false, lead: undefined, consentDocument: undefined})}
                onVoided={(consentDocuments: DocumentEnvelope[]) => voidConsentDocument(consentDocuments)}
            />
        </div>
    )
}